import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ShieldCheck, Cpu } from "lucide-react";
import { Reveal, scrollToId } from "@/components/Motion";
import { ASSETS } from "@/data/content";
import { Button } from "@/components/ui/button";

const HcaspHero = () => {
  const rhinoRef = useRef(null);
  const rhinoIn = useInView(rhinoRef, { once: true, amount: 0.2 });
  return (
    <section
      id="hcasp-hero"
      data-testid="hcasp-hero"
      className="relative min-h-screen flex items-center noise overflow-hidden bg-[#07070c]"
    >
      <div className="absolute inset-0 grid-lines opacity-40" />
      <div
        className="absolute right-0 top-1/2 -translate-y-1/2 h-[640px] w-[640px] rounded-full blur-[140px] opacity-40 bg-[#4d4479]"
        aria-hidden
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full grid lg:grid-cols-2 gap-12 items-center py-28">
        <div>
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#3d414f] bg-[#404452]/40 px-4 py-1.5">
              <ShieldCheck size={14} className="text-[#16fcfb]" />
              <span className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#97b8ff]">
                Flagship Certification
              </span>
            </div>
          </Reveal>

          <h1
            className="mt-6 font-display font-900 tracking-tight leading-[0.95] text-[#97b8ff] whitespace-nowrap text-glow-cyan"
            style={{ fontSize: "clamp(2.25rem, 5vw, 4.25rem)" }}
          >
            hackTonomy
          </h1>

          <Reveal delay={0.15}>
            <h2 className="mt-4 font-display font-600 text-2xl md:text-3xl lg:text-4xl text-[#97b8ff] leading-tight">
              Certified AI Security
              <br />
              Professional{" "}
              <span className="text-[#16fcfb] text-glow-cyan">(HCASP)</span>
            </h2>
          </Reveal>

          <Reveal delay={0.25}>
            <p className="mt-7 font-body text-lg text-white/70 max-w-lg leading-relaxed">
              An advanced, vendor neutral credential validating your mastery of
              offensive and defensive AI security across Five rigorous domains.
            </p>
          </Reveal>

          <Reveal delay={0.35}>
            <div className="mt-9 flex flex-wrap gap-4">
              <Button
                data-testid="hcasp-enroll-btn"
                onClick={() => scrollToId("pricing")}
                className="rounded-full h-12 px-8 bg-[#16fcfb] text-[#07070c] hover:bg-[#97b8ff] font-mono2 text-xs uppercase tracking-widest glow-cyan"
              >
                Enroll Now
              </Button>
              <Button
                data-testid="hcasp-curriculum-btn"
                onClick={() => scrollToId("curriculum")}
                variant="outline"
                className="rounded-full h-12 px-8 border-[#4d4479] bg-transparent text-white hover:bg-[#404452] font-mono2 text-xs uppercase tracking-widest"
              >
                <Cpu size={15} className="mr-2" /> View Curriculum
              </Button>
            </div>
          </Reveal>
        </div>

        <motion.div
          ref={rhinoRef}
          initial={{ opacity: 0, scale: 0.9, x: 40 }}
          animate={rhinoIn ? { opacity: 1, scale: 1, x: 0 } : { opacity: 0, scale: 0.9, x: 40 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative flex justify-center lg:justify-end"
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-[360px] w-[360px] rounded-full bg-[#16fcfb]/10 blur-3xl" />
          </div>
          <motion.img
            src={ASSETS.rhino}
            alt="Hacktonomy robotic rhino mascot"
            className="relative w-[78%] md:w-[80%] object-contain"
            style={{ filter: "drop-shadow(0 24px 60px rgba(22,252,251,0.3))" }}
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          />
        </motion.div>
      </div>
    </section>
  );
};

export default HcaspHero;
